import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../services/adminApiService';
import { useAppSelector } from '../../store/hooks';
import './AdminComponents.css';

interface Product {
  id: number;
  name: string;
  type: string;
  customerType: 'PERSONAL' | 'BUSINESS';
  description?: string;
  isAvailable: boolean;
  registrationFee?: number;
  registrationFeeType?: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
  requiresVerification?: boolean;
  syncRetailSubscriptionPlan?: boolean;
  registrationPaymentMethods?: 'ALL' | 'STRIPE' | 'CRYPTO';
  retailPermissionTemplate?: 'DEFAULT_RETAIL' | 'MARKET_SIGNALS_BASIC' | 'NONE';
  adminPermissionTemplate?: 'NONE' | 'OPERATIONS_MANAGER' | 'COMPLIANCE_OFFICER' | 'CUSTOMER_SUPPORT' | 'FINANCIAL_ANALYST' | 'RISK_MANAGER' | 'AUDITOR';
  retailPermissionAdds?: string[];
  retailPermissionRemoves?: string[];
  adminPermissionAdds?: string[];
  adminPermissionRemoves?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface RetailSubscriptionPlan {
  id: number;
  name: string;
  priceMonthly: number;
  currency: string;
  features?: string | null;
  active?: boolean;
}

interface PermissionTemplate {
  id: number;
  name: string;
  type: 'RETAIL' | 'ADMIN';
  description?: string;
  retailPermissions?: string[];
  adminPermissions?: string[];
  isActive?: boolean;
}

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [retailSubscriptionPlans, setRetailSubscriptionPlans] = useState<RetailSubscriptionPlan[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<{
    retailTemplates: string[];
    adminTemplates: string[];
    retailPermissions: string[];
    adminPermissions: string[];
  }>({
    retailTemplates: ['DEFAULT_RETAIL', 'MARKET_SIGNALS_BASIC', 'NONE'],
    adminTemplates: ['NONE', 'OPERATIONS_MANAGER', 'COMPLIANCE_OFFICER', 'CUSTOMER_SUPPORT', 'FINANCIAL_ANALYST', 'RISK_MANAGER', 'AUDITOR'],
    retailPermissions: [],
    adminPermissions: []
  });
  const [retailPermSearch, setRetailPermSearch] = useState('');
  const [adminPermSearch, setAdminPermSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'products' | 'templates'>('products');
  const [permissionTemplates, setPermissionTemplates] = useState<PermissionTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PermissionTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'RETAIL' as 'RETAIL' | 'ADMIN',
    description: '',
    retailPermissions: [] as string[],
    adminPermissions: [] as string[]
  });
  const [templatePermSearch, setTemplatePermSearch] = useState('');
  const pageSize = 20;

  const adminUser = useAppSelector(state => state.auth.adminUser);
  const hasConfigurePermission = adminUser?.permissions?.includes('CONFIGURE_PRODUCTS') || adminUser?.role === 'SUPER_ADMIN';

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    customerType: 'PERSONAL' as 'PERSONAL' | 'BUSINESS',
    description: '',
    isAvailable: true,
    registrationFee: 0,
    registrationFeeType: 'FIXED_ONE_OFF' as 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY',
    requiresVerification: true,
    syncRetailSubscriptionPlan: false,
    registrationPaymentMethods: 'ALL' as 'ALL' | 'STRIPE' | 'CRYPTO',
    retailPermissionTemplate: 'DEFAULT_RETAIL' as 'DEFAULT_RETAIL' | 'MARKET_SIGNALS_BASIC' | 'NONE',
    adminPermissionTemplate: 'NONE' as 'NONE' | 'OPERATIONS_MANAGER' | 'COMPLIANCE_OFFICER' | 'CUSTOMER_SUPPORT' | 'FINANCIAL_ANALYST' | 'RISK_MANAGER' | 'AUDITOR',
    retailPermissionAdds: [] as string[],
    retailPermissionRemoves: [] as string[],
    adminPermissionAdds: [] as string[],
    adminPermissionRemoves: [] as string[],
    selectedSubscriptionPlanId: ''
  });

  useEffect(() => {
    loadProducts();
  }, [currentPage, statusFilter, searchQuery]);

  useEffect(() => {
    if (showModal) {
      loadRetailSubscriptionPlans();
      loadPermissionOptions();
    }
  }, [showModal]);

  useEffect(() => {
    if (activeTab === 'templates') {
      loadPermissionOptions();
      loadPermissionTemplates();
    }
  }, [activeTab]);

  const loadPermissionOptions = async () => {
    try {
      const response = await adminApiService.getProductPermissionOptions();
      if (response.success && response.data) {
        setPermissionOptions(response.data);
      }
    } catch (err) {
      console.error('Error loading permission options:', err);
    }
  };

  const loadPermissionTemplates = async () => {
    try {
      const response = await adminApiService.getPermissionTemplates();
      if (response.success && Array.isArray(response.data)) {
        setPermissionTemplates(response.data as PermissionTemplate[]);
      }
    } catch (err) {
      console.error('Error loading permission templates:', err);
    }
  };

  const isRegistrationProductPlan = (plan: RetailSubscriptionPlan): boolean => {
    if (!plan.features || typeof plan.features !== 'string') return false;
    try {
      const parsed = JSON.parse(plan.features) as Record<string, unknown>;
      return parsed?.planKind === 'registration_product';
    } catch {
      return false;
    }
  };

  const loadRetailSubscriptionPlans = async () => {
    try {
      const response = await adminApiService.getRetailPlans();
      if (response.success && Array.isArray(response.data)) {
        const plans = response.data.filter((plan: RetailSubscriptionPlan) => plan.active !== false);
        setRetailSubscriptionPlans(plans);
      }
    } catch (err) {
      console.error('Error loading retail subscription plans:', err);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApiService.getProducts(currentPage, pageSize, statusFilter || undefined, searchQuery || undefined);
      
      if (response.success && response.data) {
        const data = response.data;
        setProducts(data.content || data.items || []);
        setTotalElements(data.totalElements || data.total || 0);
        setTotalPages(data.totalPages || 0);
      } else {
        setError(response.error || 'Failed to load products');
      }
    } catch (err: any) {
      console.error('Error loading products:', err);
      setError(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      type: '',
      customerType: 'PERSONAL',
      description: '',
      isAvailable: true,
      registrationFee: 0,
      registrationFeeType: 'FIXED_ONE_OFF',
      requiresVerification: true,
      syncRetailSubscriptionPlan: false,
      registrationPaymentMethods: 'ALL',
      retailPermissionTemplate: 'DEFAULT_RETAIL',
      adminPermissionTemplate: 'NONE',
      retailPermissionAdds: [],
      retailPermissionRemoves: [],
      adminPermissionAdds: [],
      adminPermissionRemoves: [],
      selectedSubscriptionPlanId: ''
    });
    setShowModal(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      type: product.type,
      customerType: product.customerType,
      description: product.description || '',
      isAvailable: product.isAvailable,
      registrationFee: Number(product.registrationFee || 0),
      registrationFeeType: product.registrationFeeType || 'FIXED_ONE_OFF',
      requiresVerification: product.requiresVerification !== false,
      syncRetailSubscriptionPlan: product.syncRetailSubscriptionPlan === true,
      registrationPaymentMethods: product.registrationPaymentMethods || 'ALL',
      retailPermissionTemplate: product.retailPermissionTemplate || 'DEFAULT_RETAIL',
      adminPermissionTemplate: product.adminPermissionTemplate || 'NONE',
      retailPermissionAdds: product.retailPermissionAdds || [],
      retailPermissionRemoves: product.retailPermissionRemoves || [],
      adminPermissionAdds: product.adminPermissionAdds || [],
      adminPermissionRemoves: product.adminPermissionRemoves || [],
      selectedSubscriptionPlanId: ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.name.trim()) {
        setError('Product name is required');
        setLoading(false);
        return;
      }
      if (!formData.type.trim()) {
        setError('Product type is required');
        setLoading(false);
        return;
      }
      if (formData.registrationFeeType !== 'NO_FEE' && Number(formData.registrationFee) <= 0) {
        setError('Registration fee must be greater than zero for paid fee types');
        setLoading(false);
        return;
      }
      if (formData.syncRetailSubscriptionPlan) {
        if (formData.customerType !== 'PERSONAL') {
          setError('Retail plan sync is only available for Personal customer type');
          setLoading(false);
          return;
        }
        if (formData.registrationFeeType === 'NO_FEE') {
          setError('Retail plan sync requires a paid registration fee (not No Fee)');
          setLoading(false);
          return;
        }
      }

      const payload = {
        name: formData.name,
        type: formData.type,
        customerType: formData.customerType,
        description: formData.description,
        isAvailable: formData.isAvailable,
        registrationFee: formData.registrationFee,
        registrationFeeType: formData.registrationFeeType,
        requiresVerification: formData.requiresVerification,
        syncRetailSubscriptionPlan: formData.syncRetailSubscriptionPlan,
        registrationPaymentMethods: formData.registrationPaymentMethods,
        retailPermissionTemplate: formData.retailPermissionTemplate,
        adminPermissionTemplate: formData.adminPermissionTemplate,
        retailPermissionAdds: formData.retailPermissionAdds,
        retailPermissionRemoves: formData.retailPermissionRemoves,
        adminPermissionAdds: formData.adminPermissionAdds,
        adminPermissionRemoves: formData.adminPermissionRemoves
      };

      let response;
      if (editingProduct) {
        response = await adminApiService.updateProduct(editingProduct.id, payload);
      } else {
        response = await adminApiService.createProduct(payload);
      }

      if (response.success) {
        setShowModal(false);
        setEditingProduct(null);
        await loadProducts();
      } else {
        setError(response.error || `Failed to ${editingProduct ? 'update' : 'create'} product`);
      }
    } catch (err: any) {
      console.error('Error saving product:', err);
      setError(err.response?.data?.message || `Failed to ${editingProduct ? 'update' : 'create'} product`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (product: Product) => {
    if (!window.confirm(`Are you sure you want to ${product.isAvailable ? 'deactivate' : 'activate'} this product?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await adminApiService.toggleProductStatus(product.id);
      
      if (response.success) {
        await loadProducts();
      } else {
        setError(response.error || 'Failed to toggle product status');
      }
    } catch (err: any) {
      console.error('Error toggling product status:', err);
      setError(err.response?.data?.message || 'Failed to toggle product status');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (product: Product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await adminApiService.deleteProduct(product.id);
      
      if (response.success) {
        await loadProducts();
      } else {
        setError(response.error || 'Failed to delete product');
      }
    } catch (err: any) {
      console.error('Error deleting product:', err);
      setError(err.response?.data?.message || 'Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setCurrentPage(0);
    loadProducts();
  };

  const togglePermission = (field: 'retailPermissionAdds' | 'retailPermissionRemoves' | 'adminPermissionAdds' | 'adminPermissionRemoves', permission: string) => {
    const current = formData[field];
    const exists = current.includes(permission);
    const updated = exists ? current.filter((p) => p !== permission) : [...current, permission];
    const oppositeField =
      field === 'retailPermissionAdds'
        ? 'retailPermissionRemoves'
        : field === 'retailPermissionRemoves'
          ? 'retailPermissionAdds'
          : field === 'adminPermissionAdds'
            ? 'adminPermissionRemoves'
            : 'adminPermissionAdds';
    const oppositeCurrent = formData[oppositeField];
    const oppositeUpdated = oppositeCurrent.filter((p) => p !== permission);
    setFormData({ ...formData, [field]: updated, [oppositeField]: oppositeUpdated });
  };

  const toggleTemplatePermission = (permission: string) => {
    if (templateForm.type === 'RETAIL') {
      const exists = templateForm.retailPermissions.includes(permission);
      setTemplateForm({
        ...templateForm,
        retailPermissions: exists
          ? templateForm.retailPermissions.filter((p) => p !== permission)
          : [...templateForm.retailPermissions, permission]
      });
      return;
    }
    const exists = templateForm.adminPermissions.includes(permission);
    setTemplateForm({
      ...templateForm,
      adminPermissions: exists
        ? templateForm.adminPermissions.filter((p) => p !== permission)
        : [...templateForm.adminPermissions, permission]
    });
  };

  const handleCreateTemplate = async () => {
    try {
      if (!templateForm.name.trim()) {
        setError('Template name is required');
        return;
      }
      const payload = {
        description: templateForm.description,
        retailPermissions: templateForm.type === 'RETAIL' ? templateForm.retailPermissions : [],
        adminPermissions: templateForm.type === 'ADMIN' ? templateForm.adminPermissions : []
      };
      const response = editingTemplate
        ? await adminApiService.updatePermissionTemplate(editingTemplate.id, payload)
        : await adminApiService.createPermissionTemplate({
            name: templateForm.name.trim(),
            type: templateForm.type,
            ...payload
          });
      if (!response.success) {
        setError(response.error || `Failed to ${editingTemplate ? 'update' : 'create'} permission template`);
        return;
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        type: 'RETAIL',
        description: '',
        retailPermissions: [],
        adminPermissions: []
      });
      await loadPermissionTemplates();
      await loadPermissionOptions();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to ${editingTemplate ? 'update' : 'create'} permission template`);
    }
  };

  const openEditTemplateModal = (template: PermissionTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      type: template.type,
      description: template.description || '',
      retailPermissions: template.retailPermissions || [],
      adminPermissions: template.adminPermissions || []
    });
    setTemplatePermSearch('');
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (id: number, name: string) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      const response = await adminApiService.deletePermissionTemplate(id);
      if (!response.success) {
        setError(response.error || 'Failed to delete permission template');
        return;
      }
      await loadPermissionTemplates();
      await loadPermissionOptions();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete permission template');
    }
  };

  const isMobile = window.innerWidth <= 768;
  const filteredRetailPermissions = permissionOptions.retailPermissions.filter((p) =>
    p.toLowerCase().includes(retailPermSearch.toLowerCase())
  );
  const filteredAdminPermissions = permissionOptions.adminPermissions.filter((p) =>
    p.toLowerCase().includes(adminPermSearch.toLowerCase())
  );
  const filteredTemplatePermissions = (templateForm.type === 'RETAIL'
    ? permissionOptions.retailPermissions
    : permissionOptions.adminPermissions
  ).filter((p) => p.toLowerCase().includes(templatePermSearch.toLowerCase()));

  return (
    <div className="admin-content-container">
      <div className="admin-content-header">
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1f2937' }}>Product Management</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Manage banking and fintech products</p>
        </div>
        {hasConfigurePermission && (
          <button
            onClick={handleCreate}
            className="admin-primary-button"
            style={{ 
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            + New Product
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <button
          className="admin-secondary-button"
          style={{ backgroundColor: activeTab === 'products' ? '#e7038e' : undefined, color: activeTab === 'products' ? '#fff' : undefined }}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button
          className="admin-secondary-button"
          style={{ backgroundColor: activeTab === 'templates' ? '#e7038e' : undefined, color: activeTab === 'templates' ? '#fff' : undefined }}
          onClick={() => setActiveTab('templates')}
        >
          Permission Templates
        </button>
      </div>

      {error && (
        <div className="admin-error-alert" style={{ marginBottom: '16px' }}>
          <p>{error}</p>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
      )}

      <div style={{ display: activeTab === 'products' ? 'block' : 'none' }}>
      {/* Filters */}
      <div className="admin-card" style={{ marginBottom: '16px', padding: '16px' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr auto', 
          gap: '12px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              Search Products
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
              placeholder="Search by name or type..."
              className="admin-input"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-input"
              style={{ width: '100%' }}
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <button
            onClick={handleApplyFilters}
            className="admin-primary-button"
            style={{ padding: '10px 20px', whiteSpace: 'nowrap' }}
          >
            Apply Filters
          </button>
        </div>
      </div>
      </div>

      {activeTab === 'templates' && (
        <div className="admin-card" style={{ padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>Permission Templates</h3>
            <button className="admin-primary-button" onClick={() => setShowTemplateModal(true)}>
              + New Template
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {permissionTemplates.map((template) => (
                  <tr key={template.id}>
                    <td style={{ fontWeight: 600 }}>{template.name}</td>
                    <td>{template.type}</td>
                    <td>{template.description || '-'}</td>
                    <td>
                      {(template.type === 'RETAIL' ? template.retailPermissions : template.adminPermissions)?.length || 0}
                    </td>
                    <td>
                      <button
                        className="admin-secondary-button"
                        style={{ padding: '6px 12px', fontSize: '12px', marginRight: '8px' }}
                        onClick={() => openEditTemplateModal(template)}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-delete-button"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        onClick={() => handleDeleteTemplate(template.id, template.name)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {permissionTemplates.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>
                      No templates yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="admin-card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="admin-loading-spinner"></div>
            <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#6b7280', fontSize: '16px' }}>No products found</p>
            {hasConfigurePermission && (
              <button
                onClick={handleCreate}
                className="admin-primary-button"
                style={{ marginTop: '16px' }}
              >
                Create First Product
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Customer Type</th>
                    <th>Status</th>
                    <th>Fee Type</th>
                    <th>Registration Fee</th>
                    <th>Verification</th>
                    <th>Retail plan sync</th>
                    <th>Reg payment methods</th>
                    <th>Description</th>
                    {hasConfigurePermission && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td style={{ fontWeight: 500 }}>{product.name}</td>
                      <td>{product.type}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 500,
                          backgroundColor: product.customerType === 'PERSONAL' ? '#e0e7ff' : '#fef3c7',
                          color: product.customerType === 'PERSONAL' ? '#4338ca' : '#92400e'
                        }}>
                          {product.customerType}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          backgroundColor: product.isAvailable ? '#d1fae5' : '#fee2e2',
                          color: product.isAvailable ? '#065f46' : '#991b1b'
                        }}>
                          {product.isAvailable ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td>{product.registrationFeeType || 'FIXED_ONE_OFF'}</td>
                      <td>{`EUR ${Number(product.registrationFee || 0).toFixed(2)}`}</td>
                      <td>{product.requiresVerification === false ? 'Off' : 'On'}</td>
                      <td>{product.syncRetailSubscriptionPlan === true ? 'On' : 'Off'}</td>
                      <td>{product.registrationPaymentMethods || 'ALL'}</td>
                      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.description || '-'}
                      </td>
                      {hasConfigurePermission && (
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleEdit(product)}
                              className="admin-secondary-button"
                              style={{ padding: '6px 12px', fontSize: '12px' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleStatus(product)}
                              className="admin-secondary-button"
                              style={{ 
                                padding: '6px 12px', 
                                fontSize: '12px',
                                backgroundColor: product.isAvailable ? '#fef3c7' : '#d1fae5',
                                color: product.isAvailable ? '#92400e' : '#065f46'
                              }}
                            >
                              {product.isAvailable ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDelete(product)}
                              className="admin-delete-button"
                              style={{ padding: '6px 12px', fontSize: '12px' }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '16px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} products
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="admin-secondary-button"
                    style={{ padding: '8px 16px' }}
                  >
                    Previous
                  </button>
                  <span style={{ 
                    padding: '8px 16px', 
                    backgroundColor: '#e7038e', 
                    color: 'white', 
                    borderRadius: '6px',
                    fontWeight: 500
                  }}>
                    {currentPage + 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="admin-secondary-button"
                    style={{ padding: '8px 16px' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={() => !loading && setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingProduct ? 'Edit Product' : 'New Product'}</h3>
              <button
                className="admin-modal-close"
                onClick={() => !loading && setShowModal(false)}
                disabled={loading}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="admin-input"
                  placeholder="e.g., Premium Savings Account"
                  disabled={loading}
                />
              </div>
              <div className="admin-form-group">
                <label>Product Type *</label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="admin-input"
                  placeholder="e.g., SAVINGS, CHECKING, INVESTMENT"
                  disabled={loading}
                />
              </div>
              <div className="admin-form-group">
                <label>Customer Type *</label>
                <select
                  value={formData.customerType}
                  onChange={(e) => {
                    const ct = e.target.value as 'PERSONAL' | 'BUSINESS';
                    setFormData({
                      ...formData,
                      customerType: ct,
                      syncRetailSubscriptionPlan: ct === 'BUSINESS' ? false : formData.syncRetailSubscriptionPlan
                    });
                  }}
                  className="admin-input"
                  disabled={loading}
                >
                  <option value="PERSONAL">Personal</option>
                  <option value="BUSINESS">Business</option>
                </select>
              </div>
              <div className="admin-form-group">
                <label>Fee Type *</label>
                <select
                  value={formData.registrationFeeType}
                  onChange={(e) => {
                    const nextType = e.target.value as 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
                    setFormData({
                      ...formData,
                      registrationFeeType: nextType,
                      registrationFee: nextType === 'NO_FEE' ? 0 : formData.registrationFee,
                      syncRetailSubscriptionPlan: nextType === 'NO_FEE' ? false : formData.syncRetailSubscriptionPlan,
                      selectedSubscriptionPlanId: nextType === 'NO_FEE' ? '' : formData.selectedSubscriptionPlanId
                    });
                  }}
                  className="admin-input"
                  disabled={loading}
                >
                  <option value="NO_FEE">No Fee</option>
                  <option value="FIXED_ONE_OFF">Fixed One-off Fee</option>
                  <option value="RECURRING_MONTHLY">Recurring Monthly Fee</option>
                </select>
              </div>
              {formData.registrationFeeType !== 'NO_FEE' && (
                <div className="admin-form-group">
                  <label>Subscription Model (optional)</label>
                  <select
                    value={formData.selectedSubscriptionPlanId}
                    onChange={(e) => {
                      const selectedPlanId = e.target.value;
                      const selectedPlan = retailSubscriptionPlans.find((plan) => String(plan.id) === selectedPlanId);
                      setFormData({
                        ...formData,
                        selectedSubscriptionPlanId: selectedPlanId,
                        registrationFee: selectedPlan ? Number(selectedPlan.priceMonthly || 0) : formData.registrationFee
                      });
                    }}
                    className="admin-input"
                    disabled={loading}
                  >
                    <option value="">Custom fee (no linked model)</option>
                    {retailSubscriptionPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - EUR {Number(plan.priceMonthly || 0).toFixed(2)}/month{isRegistrationProductPlan(plan) ? ' (product tier)' : ''}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#64748b' }}>
                    Pick an existing subscription model to auto-fill registration fee.
                  </small>
                </div>
              )}
              <div className="admin-form-group">
                <label>Registration Fee (EUR) *</label>
                <input
                  type="number"
                  value={formData.registrationFee}
                  onChange={(e) => setFormData({
                    ...formData,
                    registrationFee: Number(e.target.value),
                    selectedSubscriptionPlanId: ''
                  })}
                  className="admin-input"
                  min="0"
                  step="0.01"
                  placeholder="e.g., 25.00"
                  required
                  disabled={loading || formData.registrationFeeType === 'NO_FEE'}
                />
                {formData.registrationFeeType === 'NO_FEE' && (
                  <small style={{ color: '#64748b' }}>No payment will be required for this product.</small>
                )}
                {formData.registrationFeeType === 'RECURRING_MONTHLY' && (
                  <small style={{ color: '#64748b' }}>This amount will be charged monthly.</small>
                )}
              </div>
              <div className="admin-form-group">
                <label>Registration Payment Methods *</label>
                <select
                  value={formData.registrationPaymentMethods}
                  onChange={(e) => {
                    const value = e.target.value as 'ALL' | 'STRIPE' | 'CRYPTO';
                    setFormData({ ...formData, registrationPaymentMethods: value });
                  }}
                  className="admin-input"
                  disabled={loading}
                >
                  <option value="ALL">All (Stripe + Crypto)</option>
                  <option value="STRIPE">Stripe only</option>
                  <option value="CRYPTO">Crypto only</option>
                </select>
                <small style={{ color: '#64748b' }}>
                  Controls which payment methods appear in registration flow for this product.
                </small>
              </div>
              <div className="admin-form-group">
                <label>Retail Permission Template</label>
                <select
                  value={formData.retailPermissionTemplate}
                  onChange={(e) => setFormData({ ...formData, retailPermissionTemplate: e.target.value as 'DEFAULT_RETAIL' | 'MARKET_SIGNALS_BASIC' | 'NONE' })}
                  className="admin-input"
                  disabled={loading}
                >
                  {permissionOptions.retailTemplates.map((template) => (
                    <option key={template} value={template}>{template}</option>
                  ))}
                </select>
              </div>
              <div className="admin-form-group">
                <label>Retail Permission Overrides</label>
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Search retail permissions..."
                  value={retailPermSearch}
                  onChange={(e) => setRetailPermSearch(e.target.value)}
                  disabled={loading}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px' }}>
                  {filteredRetailPermissions.map((perm) => (
                    <div key={perm} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#374151' }}>{perm}</span>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <label style={{ fontSize: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formData.retailPermissionAdds.includes(perm)}
                            onChange={() => togglePermission('retailPermissionAdds', perm)}
                            disabled={loading}
                            style={{ marginRight: '4px' }}
                          />
                          Add
                        </label>
                        <label style={{ fontSize: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formData.retailPermissionRemoves.includes(perm)}
                            onChange={() => togglePermission('retailPermissionRemoves', perm)}
                            disabled={loading}
                            style={{ marginRight: '4px' }}
                          />
                          Remove
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="admin-form-group">
                <label>Admin Permission Template</label>
                <select
                  value={formData.adminPermissionTemplate}
                  onChange={(e) => setFormData({ ...formData, adminPermissionTemplate: e.target.value as 'NONE' | 'OPERATIONS_MANAGER' | 'COMPLIANCE_OFFICER' | 'CUSTOMER_SUPPORT' | 'FINANCIAL_ANALYST' | 'RISK_MANAGER' | 'AUDITOR' })}
                  className="admin-input"
                  disabled={loading}
                >
                  {permissionOptions.adminTemplates.map((template) => (
                    <option key={template} value={template}>{template}</option>
                  ))}
                </select>
                <small style={{ color: '#64748b' }}>
                  Applied when this product is used to provision an admin user in onboarding flows.
                </small>
              </div>
              <div className="admin-form-group">
                <label>Admin Permission Overrides</label>
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Search admin permissions..."
                  value={adminPermSearch}
                  onChange={(e) => setAdminPermSearch(e.target.value)}
                  disabled={loading}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px' }}>
                  {filteredAdminPermissions.map((perm) => (
                    <div key={perm} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#374151' }}>{perm}</span>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <label style={{ fontSize: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formData.adminPermissionAdds.includes(perm)}
                            onChange={() => togglePermission('adminPermissionAdds', perm)}
                            disabled={loading}
                            style={{ marginRight: '4px' }}
                          />
                          Add
                        </label>
                        <label style={{ fontSize: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formData.adminPermissionRemoves.includes(perm)}
                            onChange={() => togglePermission('adminPermissionRemoves', perm)}
                            disabled={loading}
                            style={{ marginRight: '4px' }}
                          />
                          Remove
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="admin-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="admin-input"
                  rows={4}
                  placeholder="Product description..."
                  disabled={loading}
                />
              </div>
              <div className="admin-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isAvailable}
                    onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                    disabled={loading}
                    style={{ marginRight: '8px' }}
                  />
                  Available for registration
                </label>
              </div>
              <div className="admin-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.requiresVerification}
                    onChange={(e) => setFormData({ ...formData, requiresVerification: e.target.checked })}
                    disabled={loading}
                    style={{ marginRight: '8px' }}
                  />
                  Require SUMSUB / document verification during registration
                </label>
              </div>
              <div className="admin-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.syncRetailSubscriptionPlan}
                    onChange={(e) => setFormData({ ...formData, syncRetailSubscriptionPlan: e.target.checked })}
                    disabled={loading || formData.customerType !== 'PERSONAL' || formData.registrationFeeType === 'NO_FEE'}
                    style={{ marginRight: '8px' }}
                  />
                  Sync to retail subscription plan (Subscriptions page)
                </label>
                <small style={{ display: 'block', color: '#64748b', marginTop: '6px' }}>
                  Personal products with a paid fee only. Creates or updates a matching row under Super Admin → Subscriptions
                  so users can be granted that plan and pass subscription enforcement.
                </small>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-secondary-button"
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="admin-primary-button"
                onClick={handleSave}
                disabled={loading || !formData.name.trim() || !formData.type.trim() || (formData.registrationFeeType !== 'NO_FEE' && Number(formData.registrationFee) <= 0)}
              >
                {loading ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showTemplateModal && (
        <div className="admin-modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingTemplate ? 'Edit Permission Template' : 'New Permission Template'}</h3>
              <button className="admin-modal-close" onClick={() => setShowTemplateModal(false)}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label>Template Name *</label>
                <input
                  type="text"
                  className="admin-input"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value.toUpperCase() })}
                  disabled={!!editingTemplate}
                />
              </div>
              <div className="admin-form-group">
                <label>Template Type *</label>
                <select
                  className="admin-input"
                  value={templateForm.type}
                  onChange={(e) => setTemplateForm({
                    ...templateForm,
                    type: e.target.value as 'RETAIL' | 'ADMIN',
                    retailPermissions: [],
                    adminPermissions: [],
                  })}
                  disabled={!!editingTemplate}
                >
                  <option value="RETAIL">Retail</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div className="admin-form-group">
                <label>Description</label>
                <textarea
                  className="admin-input"
                  rows={3}
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                />
              </div>
              <div className="admin-form-group">
                <label>Permissions</label>
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Search permissions..."
                  value={templatePermSearch}
                  onChange={(e) => setTemplatePermSearch(e.target.value)}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px' }}>
                  {filteredTemplatePermissions.map((perm) => {
                    const checked = templateForm.type === 'RETAIL'
                      ? templateForm.retailPermissions.includes(perm)
                      : templateForm.adminPermissions.includes(perm);
                    return (
                      <label key={perm} style={{ display: 'block', fontSize: '12px', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTemplatePermission(perm)}
                          style={{ marginRight: '6px' }}
                        />
                        {perm}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-secondary-button"
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                }}
              >
                Cancel
              </button>
              <button className="admin-primary-button" onClick={handleCreateTemplate}>
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;

